import { Injectable } from '@angular/core'

import type { Observable } from 'rxjs'
import { BehaviorSubject, map } from 'rxjs'

import type { MagazineDto } from '../../api/responses'
import type {
  HomeState,
  PaginatedArticles,
} from '../../pages/home/models/home.models'
import type { ArticleType } from '../../types/types'

@Injectable({
  providedIn: 'root',
})
export class HomeStateService {
  private readonly state = new BehaviorSubject<HomeState>({
    magazines: [],
    searchTerm: null,
    selectedMagazineIds: [],
    selectedAuthorIds: [],
    selectedArticleTypes: [],
    loading: false,
    articles: [],
    totalDocs: 0,
    currentPage: 1,
    itemsPerPage: 10,
    totalPages: 1,
    sortDirection: 'desc',
    showArticles: false,
  })

  // Selectors
  readonly magazines$ = this.select(state => state.magazines)
  readonly searchTerm$ = this.select(state => state.searchTerm)
  readonly selectedMagazineIds$ = this.select(
    state => state.selectedMagazineIds
  )
  readonly selectedAuthorIds$ = this.select(state => state.selectedAuthorIds)
  readonly selectedArticleTypes$ = this.select(
    state => state.selectedArticleTypes
  )
  readonly loading$ = this.select(state => state.loading)
  readonly sortDirection$ = this.select(state => state.sortDirection)
  readonly itemsPerPage$ = this.select(state => state.itemsPerPage)
  readonly showArticles$ = this.select(state => state.showArticles)
  readonly currentPage$ = this.select(state => state.currentPage)
  readonly totalPages$ = this.select(state => state.totalPages)
  readonly articles$ = this.select(state => state.articles)
  readonly totalDocs$ = this.select(state => state.totalDocs)

  // State Updates
  setMagazines(magazines: MagazineDto[]): void {
    this.updateState({ magazines })
  }

  setSearchTerm(searchTerm: string | null): void {
    this.updateState({ searchTerm, currentPage: 1 })
  }

  setSelectedMagazineIds(selectedMagazineIds: number[]): void {
    this.updateState({ selectedMagazineIds, currentPage: 1 })
  }

  setSelectedAuthorIds(selectedAuthorIds: number[]): void {
    this.updateState({ selectedAuthorIds, currentPage: 1 })
  }

  setSelectedArticleTypes(selectedArticleTypes: ArticleType[]): void {
    this.updateState({ selectedArticleTypes, currentPage: 1 })
  }

  setLoading(loading: boolean): void {
    this.updateState({ loading })
  }

  setArticles(result: PaginatedArticles): void {
    this.updateState({
      articles: result.articles,
      totalDocs: result.totalDocs,
      totalPages: result.totalPages,
      currentPage: result.currentPage,
    })
  }

  setCurrentPage(currentPage: number): void {
    this.updateState({ currentPage })
  }

  setItemsPerPage(itemsPerPage: number): void {
    this.updateState({ itemsPerPage, currentPage: 1 })
  }

  toggleSortDirection(): void {
    const currentDirection = this.state.value.sortDirection
    this.updateState({
      sortDirection: currentDirection === 'asc' ? 'desc' : 'asc',
      currentPage: 1,
    })
  }

  resetMagazineFilter(): void {
    this.updateState({ selectedMagazineIds: [], currentPage: 1 })
  }

  resetAuthorFilter(): void {
    this.updateState({ selectedAuthorIds: [], currentPage: 1 })
  }

  resetArticleTypeFilter(): void {
    this.updateState({ selectedArticleTypes: [], currentPage: 1 })
  }

  private select<T>(selector: (state: HomeState) => T): Observable<T> {
    return this.state.pipe(map(selector))
  }

  private updateState(partialState: Partial<HomeState>): void {
    const newState = { ...this.state.value, ...partialState }

    const shouldShowArticles =
      !!newState.searchTerm ||
      (newState.selectedMagazineIds &&
        newState.selectedMagazineIds.length > 0) ||
      (newState.selectedArticleTypes &&
        newState.selectedArticleTypes.length > 0) ||
      (newState.selectedAuthorIds && newState.selectedAuthorIds.length > 0)

    this.state.next({ ...newState, showArticles: shouldShowArticles })
  }
}
